import React, { useState, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Download, Upload, Plus, Minus, FileText } from 'lucide-react';
import Button from '../ui/Button';
import Select from '../ui/Select';
import Input from '../ui/Input';
import { useAppStore } from '../../store';

interface BulkStockWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

interface StockOperation {
  product_type: string;
  color: string;
  size: string;
  quantity: number;
  operation: 'add' | 'subtract' | 'set';
}

const BulkStockWizard: React.FC<BulkStockWizardProps> = ({
  isOpen,
  onClose,
}) => {
  const { expressStock, addExpressStock, updateExpressStock } = useAppStore();
  const [currentStep, setCurrentStep] = useState(1);
  const [operationType, setOperationType] = useState<'add' | 'subtract' | 'set'>('add');
  const [stockOperations, setStockOperations] = useState<StockOperation[]>([]);
  const [fileInput, setFileInput] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get unique values for dropdowns
  const productTypes = [...new Set(expressStock.map(stock => stock.product_type))];
  const colors = [...new Set(expressStock.map(stock => stock.color))];
  const sizes = [...new Set(expressStock.map(stock => stock.size))];

  const handleDownloadCSV = (type: 'add' | 'subtract' | 'set') => {
    const headers = ['product_type', 'color', 'size', 'quantity'];
    const sampleData = [
      ['Remera', 'Negro', 'M', '10'],
      ['Buzo', 'Azul', 'L', '5'],
      ['Remera', 'Blanco', 'S', '15']
    ];

    const csvContent = [headers, ...sampleData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `stock_${type}_ejemplo.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'text/csv') {
      setFileInput(file);
      parseCSV(file);
    }
  };

  const parseCSV = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n');
      const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
      
      const operations: StockOperation[] = [];
      for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim()) {
          const values = lines[i].split(',').map(v => v.replace(/"/g, '').trim());
          if (values.length >= 4) {
            operations.push({
              product_type: values[0],
              color: values[1],
              size: values[2],
              quantity: parseInt(values[3]) || 0,
              operation: operationType
            });
          }
        }
      }
      setStockOperations(operations);
    };
    reader.readAsText(file);
  };

  const handleExecuteOperations = async () => {
    try {
      for (const operation of stockOperations) {
        const existingStock = expressStock.find(stock => 
          stock.product_type === operation.product_type &&
          stock.color === operation.color &&
          stock.size === operation.size
        );

        if (existingStock) {
          let newQuantity = existingStock.quantity;
          switch (operation.operation) {
            case 'add':
              newQuantity += operation.quantity;
              break;
            case 'subtract':
              newQuantity = Math.max(0, newQuantity - operation.quantity);
              break;
            case 'set':
              newQuantity = operation.quantity;
              break;
          }
          
          await updateExpressStock(existingStock.id, { quantity: newQuantity });
        } else if (operation.operation !== 'subtract') {
          await addExpressStock({
            product_type: operation.product_type as any,
            color: operation.color,
            size: operation.size as any,
            quantity: operation.operation === 'set' ? operation.quantity : operation.quantity
          });
        }
      }
      
      // Reset and close
      setStockOperations([]);
      setFileInput(null);
      setCurrentStep(1);
      onClose();
    } catch (error) {
      console.error('Error executing operations:', error);
    }
  };

  const resetWizard = () => {
    setCurrentStep(1);
    setOperationType('add');
    setStockOperations([]);
    setFileInput(null);
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />
        <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[90vh] w-[95vw] max-w-[800px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white p-6 shadow-lg overflow-y-auto">
          <Dialog.Title className="text-xl font-semibold mb-4">
            Wizard de Operaciones Masivas de Stock
          </Dialog.Title>
          
          {/* Progress Steps */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div className={`flex items-center ${currentStep >= 1 ? 'text-indigo-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  currentStep >= 1 ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-gray-300'
                }`}>
                  1
                </div>
                <span className="ml-2 text-sm font-medium">Tipo de Operación</span>
              </div>
              <div className={`flex items-center ${currentStep >= 2 ? 'text-indigo-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  currentStep >= 2 ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-gray-300'
                }`}>
                  2
                </div>
                <span className="ml-2 text-sm font-medium">Cargar Datos</span>
              </div>
              <div className={`flex items-center ${currentStep >= 3 ? 'text-indigo-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  currentStep >= 3 ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-gray-300'
                }`}>
                  3
                </div>
                <span className="ml-2 text-sm font-medium">Revisar y Ejecutar</span>
              </div>
            </div>
          </div>

          {/* Step 1: Operation Type */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-4">Selecciona el tipo de operación</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      operationType === 'add' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setOperationType('add')}
                  >
                    <div className="flex items-center space-x-3">
                      <Plus className="h-6 w-6 text-green-600" />
                      <div>
                        <h4 className="font-medium">Agregar Stock</h4>
                        <p className="text-sm text-gray-600">Sumar cantidad al stock existente</p>
                      </div>
                    </div>
                  </div>
                  
                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      operationType === 'subtract' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setOperationType('subtract')}
                  >
                    <div className="flex items-center space-x-3">
                      <Minus className="h-6 w-6 text-red-600" />
                      <div>
                        <h4 className="font-medium">Reducir Stock</h4>
                        <p className="text-sm text-gray-600">Restar cantidad del stock existente</p>
                      </div>
                    </div>
                  </div>
                  
                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      operationType === 'set' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setOperationType('set')}
                  >
                    <div className="flex items-center space-x-3">
                      <FileText className="h-6 w-6 text-blue-600" />
                      <div>
                        <h4 className="font-medium">Establecer Stock</h4>
                        <p className="text-sm text-gray-600">Definir cantidad exacta del stock</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={onClose}>
                  Cancelar
                </Button>
                <Button onClick={() => setCurrentStep(2)}>
                  Continuar
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Upload Data */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-4">Cargar datos desde CSV</h3>
                
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium mb-2">Descargar CSV de ejemplo:</h4>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<Download className="h-4 w-4" />}
                      onClick={() => handleDownloadCSV('add')}
                    >
                      CSV Agregar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<Download className="h-4 w-4" />}
                      onClick={() => handleDownloadCSV('subtract')}
                    >
                      CSV Reducir
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<Download className="h-4 w-4" />}
                      onClick={() => handleDownloadCSV('set')}
                    >
                      CSV Establecer
                    </Button>
                  </div>
                </div>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-4">
                    <Button
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Seleccionar archivo CSV
                    </Button>
                  </div>
                  <p className="mt-2 text-sm text-gray-600">
                    O arrastra y suelta el archivo aquí
                  </p>
                  {fileInput && (
                    <p className="mt-2 text-sm text-green-600">
                      Archivo cargado: {fileInput.name}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(1)}>
                  Atrás
                </Button>
                <Button 
                  onClick={() => setCurrentStep(3)}
                  disabled={stockOperations.length === 0}
                >
                  Continuar
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Review and Execute */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-4">Revisar operaciones</h3>
                
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    Se ejecutarán {stockOperations.length} operaciones de {operationType === 'add' ? 'agregar' : operationType === 'subtract' ? 'reducir' : 'establecer'} stock:
                  </p>
                </div>

                <div className="max-h-64 overflow-y-auto border rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Color</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Talle</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {stockOperations.map((op, index) => (
                        <tr key={index}>
                          <td className="px-3 py-2 text-sm text-gray-900">{op.product_type}</td>
                          <td className="px-3 py-2 text-sm text-gray-900">{op.color}</td>
                          <td className="px-3 py-2 text-sm text-gray-900">{op.size}</td>
                          <td className="px-3 py-2 text-sm text-gray-900">{op.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(2)}>
                  Atrás
                </Button>
                <Button onClick={handleExecuteOperations}>
                  Ejecutar Operaciones
                </Button>
              </div>
            </div>
          )}
          
          <Dialog.Close asChild>
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
              aria-label="Close"
              onClick={resetWizard}
            >
              <X className="h-6 w-6" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default BulkStockWizard;
